import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { Quotation } from '@tailoring-crm/shared';
import { useRejectQuotation } from '../../lib/quotations/use-quotations.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  useToast,
} from '@tailoring-crm/ui';

/**
 * They are not going ahead — and why, if anybody knows.
 *
 * The reason is OPTIONAL, which is the whole design of this dialog. A shop that is told "it is
 * over our budget" should be able to keep that, because it is the only thing a lost price is
 * good for months later; a shop whose customer simply went quiet must not be made to invent
 * something to get the document closed. A required field here would be filled with "other" a
 * hundred times and the record would be worth nothing.
 *
 * Declining closes the DOCUMENT and nothing else. The customer's deal stays exactly where it is —
 * the shop did quote them — and closing it as lost is a separate decision with its own reason,
 * taken on the pipeline board.
 */
export function RejectQuotationDialog({
  quotation,
  onOpenChange,
}: {
  /** The document being declined, or null when the dialog is closed. */
  quotation: Quotation | null;
  onOpenChange: (open: boolean) => void;
}): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const reject = useRejectQuotation();
  const [reason, setReason] = useState('');

  const submit = (): void => {
    if (!quotation) return;
    void (async () => {
      try {
        await reject.mutateAsync({ quotationId: quotation.id, reason: reason.trim() || null });
        toast({ title: t('quotations.rejected'), variant: 'success' });
        setReason('');
        onOpenChange(false);
      } catch (error) {
        toast({
          title: error instanceof Error ? error.message : t('error.unexpected'),
          variant: 'destructive',
        });
      }
    })();
  };

  return (
    <Dialog open={quotation !== null} onOpenChange={onOpenChange}>
      <DialogContent className="atelier-dialog">
        <DialogHeader>
          <DialogTitle>
            {t('quotations.reject.title', { number: quotation?.number ?? '' })}
          </DialogTitle>
          <DialogDescription>{t('quotations.reject.hint')}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="quotation-reject-reason">{t('quotations.reject.reasonLabel')}</Label>
          <Input
            id="quotation-reject-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={t('quotations.reject.reasonPlaceholder')}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button variant="destructive" disabled={reject.isPending} onClick={submit}>
            {t('quotations.reject.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default RejectQuotationDialog;
